import React from "react";
import { pricingInfo } from "../../data/pricing-page-data";
import { css, useTheme } from "@emotion/react";
import { mediaQueries, pxToRem } from "@upsun/react-component-library/src/styles/css-utils";
import { checkmarks, plus } from "@upsun/react-component-library/src/styles";
import Heading from "@upsun/react-component-library/src/components/Heading";
import { fontSize, gridCard } from "../../utils/pricing-page-styles";
import { colors } from "../../utils/colors";
import { UpsunTheme } from "@upsun/react-component-library/src/themes";
import colorsLight from "@upsun/react-component-library/src/styles/colors-light";

type UserLicensesDetailsProps = {
  currency: string;
};

const UserLicensesDetails: React.FC<UserLicensesDetailsProps> = ({
  currency,
  ...props
}) => {
  const theme = useTheme() as UpsunTheme;
  const userManagementInnerGrid = css`
    margin-top: ${pxToRem(24)};
    background: ${theme.name === "light" ? colorsLight.CanvasDefault : "#0e1113"};
    border-radius: ${pxToRem(8)};
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(12)};
    padding: ${pxToRem(20)};

    ${mediaQueries.md} {
      flex-direction: row;

      div {
        width: 50%;
      }
    }
  `;

  const smallFont = css`
    font-size: ${pxToRem(14)} !important;
  `;

  const userManagementContainer = css`
    margin-top: ${pxToRem(40)};

    ${mediaQueries.md} {
      grid-column-start: 1;
      grid-column-end: 3;
    }
  `;

  const customHr = css`
    border: none;
    border-top: ${pxToRem(1)} solid ${colors.black600};
  `;

  const checkmarksCustom = css`
    ${checkmarks};

    li::before {
      ${theme.name === "light" ? `
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='20' viewBox='0 0 24 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='tick'%3E%3Cpath id='Icon' d='M9.99999 15.1C9.69999 15.1 9.49999 15 9.29999 14.8L5.09999 10.6C4.69999 10.2 4.69999 9.60002 5.09999 9.20002C5.49999 8.80002 6.09999 8.80002 6.49999 9.20002L9.99999 12.7L17.5 5.20002C17.9 4.80002 18.5 4.80002 18.9 5.20002C19.3 5.60002 19.3 6.20002 18.9 6.60002L10.7 14.8C10.5 15 10.3 15.1 9.99999 15.1Z' fill='%234d38cc'/%3E%3C/g%3E%3C/svg%3E%0A");
      ` :
      `
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='20' viewBox='0 0 24 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='tick'%3E%3Cpath id='Icon' d='M9.99999 15.1C9.69999 15.1 9.49999 15 9.29999 14.8L5.09999 10.6C4.69999 10.2 4.69999 9.60002 5.09999 9.20002C5.49999 8.80002 6.09999 8.80002 6.49999 9.20002L9.99999 12.7L17.5 5.20002C17.9 4.80002 18.5 4.80002 18.9 5.20002C19.3 5.60002 19.3 6.20002 18.9 6.60002L10.7 14.8C10.5 15 10.3 15.1 9.99999 15.1Z' fill='%23A090FF'/%3E%3C/g%3E%3C/svg%3E%0A");
      `
    }
  }`;

  const plusCustom = css`
    ${plus};

    li::before {
      ${theme.name === "light" ? `
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='plus'%3E%3Cpath id='Icon' d='M12 6C11.4477 6 11 6.44772 11 7V11H7C6.44772 11 6 11.4477 6 12C6 12.5523 6.44772 13 7 13H11V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V13H17C17.5523 13 18 12.5523 18 12C18 11.4477 17.5523 11 17 11H13V7C13 6.44772 12.5523 6 12 6Z' fill='%234d38cc'/%3E%3C/g%3E%3C/svg%3E");
      ` :
      `
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='plus'%3E%3Cpath id='Icon' d='M12 6C11.4477 6 11 6.44772 11 7V11H7C6.44772 11 6 11.4477 6 12C6 12.5523 6.44772 13 7 13H11V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V13H17C17.5523 13 18 12.5523 18 12C18 11.4477 17.5523 11 17 11H13V7C13 6.44772 12.5523 6 12 6Z' fill='%23A090FF'/%3E%3C/g%3E%3C/svg%3E");
      `
    }
  }`;

  return (
    <div css={gridCard}>
      <div>
        <Heading level={4}>User licenses</Heading>
        <div css={fontSize}>
          {pricingInfo.userLicense[currency]}/user/month
        </div>
      </div>
      <div>
        <ul css={checkmarksCustom}>
          <li>Custom project and environment-type permissions</li>
        </ul>
      </div>
      <div css={userManagementContainer}>
        <Heading level={4}>Advanced user management</Heading>
        <hr css={customHr} />
        <div css={userManagementInnerGrid}>
          <div>
            <div>+{pricingInfo.userManagementFee[currency]}/user/month</div>
          </div>
          <div>
            <ul css={[plusCustom, smallFont]}>
              <li>Teams management</li>
              <li>MFA enforcement</li>
              <li>Single sign-on (SSO): Google, OIDC (Microsoft Entra ID, Okta, PingID, Ory)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLicensesDetails;
